import { FILTER_KEYS, hrefFor, parseQuery, toSearchParams, type TourQuery } from "./filters";

// Tek filtreli tur listesi = o filtrenin sayfası (ürün sahibi kararı, 2026-10-08): yalnızca bölge, ürün ailesi ya da
// destinasyon seçiliyse liste adresi /turlar?bolge=avrupa yerine bölge sayfası /avrupa-turlari (/en/europe-tours),
// /turlar?aile=fly-elegant yerine /fly-elegant, /turlar?kategori=italya-turlari yerine /italya-turlari. Sıralama ve sayfa
// filtre sayfasında sorgu olarak kalır (/avrupa-turlari?sirala=fiyat-artan&sayfa=2). Başka bir filtre de seçiliyse (ay,
// kalkış yeri, kod, arama …) adres /turlar?… olarak kalır ve dizine eklenmez. Saf: istemci (filtre formu, ana sayfa araması)
// ve sunucu (doğrudan açılan /turlar?bolge=… → 308) aynı kuralı kullanır.

/** Filtre sayfası olan filtreler */
export const LANDING_KEYS = ["bolge", "aile", "kategori"] as const;
export type LandingKey = (typeof LANDING_KEYS)[number];

/** "bolge:avrupa" → "/avrupa-turlari" (bu dildeki adres); yalnızca sayfası olan değerler (katalogdaki destinasyonlar) */
export type LandingMap = Record<string, string>;

/** Liste bağlantılarının kurulduğu bağlam: bu dildeki liste adresi ve filtre sayfaları */
export type ListingNav = { list: string; landings: LandingMap };

const isSet = (v: unknown) => v !== undefined && v !== false && v !== "";

/** Sorguda yalnızca tek bir filtre varsa ve o filtrenin sayfası olabilirse "bolge:avrupa"; değilse null */
export function landingKey(query: Partial<TourQuery>): string | null {
  const active = FILTER_KEYS.filter((k) => isSet(query[k]));
  if (active.length !== 1) return null;
  const key = active[0];
  return (LANDING_KEYS as readonly string[]).includes(key) ? `${key}:${String(query[key])}` : null;
}

/** Sorgunun kanonik liste adresi: tek filtreliyse o filtrenin sayfası (+ sıralama/sayfa), değilse /turlar?… */
export function listingHref(query: Partial<TourQuery>, nav: ListingNav): string {
  const key = landingKey(query);
  const landing = key ? nav.landings[key] : undefined;
  if (!landing) return hrefFor(query, nav.list);
  const qs = toSearchParams({ sirala: query.sirala, sayfa: query.sayfa }).toString();
  return qs ? `${landing}?${qs}` : landing;
}

type RawParams = Record<string, string | string[] | undefined>;
/** Liste sorgusunun tanıdığı parametreler; geri kalanlar (utm_* …) yönlendirmede aynen taşınır */
const KNOWN = new Set<string>([...FILTER_KEYS, "sirala", "sayfa", "destinasyon"]);

/**
 * Doğrudan açılan /turlar?… adresi tek filtreliyse gidilecek filtre sayfası (sıralama, sayfa ve tanınmayan parametreler
 * korunur); değilse null. Geçersiz değer varsa (ör. bolge=mars) yönlenmez: liste uyarıyı gösterir.
 */
export function landingRedirect(raw: RawParams, nav: ListingNav): string | null {
  const { query, invalid } = parseQuery(raw);
  if (invalid.length > 0) return null;
  const key = landingKey(query);
  if (!key || !nav.landings[key]) return null;
  const target = new URL(listingHref(query, nav), "http://x");
  for (const [k, v] of Object.entries(raw)) {
    if (KNOWN.has(k) || v === undefined) continue;
    for (const item of Array.isArray(v) ? v : [v]) target.searchParams.append(k, item);
  }
  return target.pathname + target.search;
}

/** URLSearchParams → sayfa searchParams biçimi (tekrarlanan anahtar dizi olur) */
export function paramsToRecord(params: URLSearchParams): RawParams {
  const out: Record<string, string | string[]> = {};
  for (const [k, v] of params) {
    const prev = out[k];
    out[k] = prev === undefined ? v : Array.isArray(prev) ? [...prev, v] : [prev, v];
  }
  return out;
}

/** Filtre sayfasının sorgusu: sabit filtre + adresteki sıralama ve sayfa (diğer parametreler yok sayılır) */
export function landingQuery(fixed: Partial<Pick<TourQuery, LandingKey>>, raw: RawParams): TourQuery {
  const { query } = parseQuery({ sirala: raw.sirala, sayfa: raw.sayfa });
  return { ...query, ...fixed };
}
